/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";

import { supabase } from "../../supabaseClient";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import { useAuth } from "../../context/AuthContext";
import { useTournamentAuth } from "../../utils/authorization";
import SelectedCenters from "./SelectedCenters";
import AddCenterForm from "./AddCenterForm";
import { useTournamentId } from "../../context/TournamentRouteContext";
import {
  listOrganizationBowlingCenters,
  organizationIdForTournament,
  unlinkOrganizationBowlingCenter,
} from "../../utility/organizationBowlingCenters";

const TournamentLocations = ({ organizationId: organizationIdProp, onCentersChange }) => {
  const tournamentId = useTournamentId();
  const { isLoaded, loadError } = useGoogleMaps();
  const { user } = useAuth();
  const [selectedCenters, setSelectedCenters] = useState([]);
  const [allCenters, setAllCenters] = useState([]);
  const [organizationId, setOrganizationId] = useState(organizationIdProp || null);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewCenterForm, setShowNewCenterForm] = useState(false);
  const [error, setError] = useState(null);

  const { canManage, loading: authLoading } = useTournamentAuth(tournamentId);

  const clearError = () => setError(null);

  useEffect(() => {
    if (loadError) {
      setError("Error loading Google Maps. Please refresh the page.");
    }
  }, [loadError]);

  useEffect(() => {
    if (organizationIdProp) {
      setOrganizationId(organizationIdProp);
      return;
    }
    if (!tournamentId) return;
    organizationIdForTournament(tournamentId)
      .then(setOrganizationId)
      .catch((err) => {
        console.error(err);
        setError("Failed to load organization");
      });
  }, [organizationIdProp, tournamentId]);

  const fetchOrgCenters = async (orgId) => {
    try {
      setError(null);
      const [orgCenters, catalog] = await Promise.all([
        listOrganizationBowlingCenters(orgId),
        supabase
          .from("bowlingCenters")
          .select("*")
          .is("deletedAt", null)
          .then(({ data, error: catalogError }) => {
            if (catalogError) throw catalogError;
            return data || [];
          }),
      ]);
      setSelectedCenters(orgCenters);
      setAllCenters(catalog);
      onCentersChange?.(orgCenters);
    } catch (err) {
      console.error("Error fetching bowling centers:", err);
      setError("Failed to load bowling centers");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!organizationId) return;
    setIsLoading(true);
    fetchOrgCenters(organizationId);
  }, [organizationId]);

  const handleCenterRemove = async (centerId) => {
    if (!organizationId) return;
    try {
      setError(null);
      await unlinkOrganizationBowlingCenter({
        organizationId,
        bowlingCenterId: centerId,
      });
      fetchOrgCenters(organizationId);
    } catch (err) {
      console.error("Error removing bowling center:", err);
      setError("Failed to remove bowling center");
    }
  };

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  if (isLoading || authLoading) {
    return (
      <div className="card-board p-6 mb-6">
        <div className="animate-pulse">Loading bowling centers...</div>
      </div>
    );
  }

  return (
    <div className="card-board p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading-display text-xl">Bowling Centers</h2>
      </div>

      {error && (
        <InlineAlert type="error" message={error} onClose={clearError} />
      )}

      <p className="text-sm text-brand-muted mb-4">
        Add the house this organization bowls at. Squads pick from that list.
      </p>

      <SelectedCenters
        selectedCenters={selectedCenters}
        onRemoveCenter={handleCenterRemove}
        isLoggedIn={!!user}
        canManage={canManage}
      />

      <AddCenterForm
        showForm={showNewCenterForm}
        onToggleForm={() => setShowNewCenterForm(!showNewCenterForm)}
        allCenters={allCenters}
        organizationId={organizationId}
        onCenterAdded={() => fetchOrgCenters(organizationId)}
        isLoggedIn={!!user}
        canManage={canManage}
      />
    </div>
  );
};

export default TournamentLocations;
